import { Tooltip } from '@odo/shared-ui';
import type { ListFilterSummary } from './listFilterSummary';
import type { ListPositionKind, ListPositionLabel } from './listPosition';

// min-w stops the stepper shifting as digits change; ring-inset because the group clips outside rings.
const SEGMENT_CLASS =
    'inline-flex min-w-[5.5rem] cursor-default items-center justify-center gap-1 px-2.5 text-xs tabular-nums text-gray-500 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-400';

// Only a current place gets the emphasised number; the stale one reads muted and italic.
const KIND_CLASS: Record<ListPositionKind, { segment: string; current: string }> = {
    inList: { segment: '', current: 'font-semibold text-gray-900' },
    leftList: { segment: 'italic', current: '' },
    notInList: { segment: '', current: '' }
};

interface ListSummaryPopoverProps {
    label: ListPositionLabel;
    summary: ListFilterSummary;
}

function ListSummaryPopover({ label, summary }: ListSummaryPopoverProps) {
    return (
        <span className="flex flex-col gap-0.5 text-left">
            <span className="font-semibold">{summary.leadText}</span>
            {label.note && <span>{label.note}</span>}
            {summary.filters.map((filter) => (
                <span key={filter.key}>
                    {filter.label}: {filter.value}
                </span>
            ))}
            <span className="font-normal text-gray-600">{summary.sortText}</span>
        </span>
    );
}

interface ListPositionIndicatorProps {
    label: ListPositionLabel;
    summary: ListFilterSummary;
}

// A button only to be focusable and nameable; hover or focus shows which list Previous and Next walk.
export function ListPositionIndicator({ label, summary }: ListPositionIndicatorProps) {
    return (
        <Tooltip content={<ListSummaryPopover label={label} summary={summary} />} side="bottom">
            <button
                type="button"
                aria-label={label.ariaLabel}
                className={`${SEGMENT_CLASS} ${KIND_CLASS[label.kind].segment}`}
            >
                <span className={KIND_CLASS[label.kind].current}>{label.current}</span>
                <span>of {label.total}</span>
            </button>
        </Tooltip>
    );
}
